import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import {
  DSAlert,
  DSBackLink,
  DSBadge,
  DSButton,
  DSCard,
  DSIcon,
  DSModal,
  DSSelect,
  DSPageHeader
} from '../design-system/primitives';
import PinGolfHoleList from '../components/PinGolfHoleList';
import PinGolfHoleSheet from '../components/PinGolfHoleSheet';
import { formatScoreAbbreviated } from '../utils/formatScore';
import { formatVsPar } from '../utils/pingolfScoring';
import { showToast } from '../components/Toast';
import './PingolfHostControls.css';
import { personName } from '../utils/personName';
import UserAvatar from '../components/UserAvatar';

function PingolfHostControls() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingLifecycle, setUpdatingLifecycle] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [operationSummary, setOperationSummary] = useState(null);
  const [draftHoles, setDraftHoles] = useState([]);
  const [savingHoles, setSavingHoles] = useState(false);
  const [showAddHoleSheet, setShowAddHoleSheet] = useState(false);
  const [newHole, setNewHole] = useState({ machineId: '', targetScore: '', maxBalls: '3' });
  const [eligibleHosts, setEligibleHosts] = useState([]);
  const [selectedHostUserId, setSelectedHostUserId] = useState('');
  const [assigningHost, setAssigningHost] = useState(false);
  const [hostRemovalTarget, setHostRemovalTarget] = useState(null);
  const [removingHostId, setRemovingHostId] = useState(null);
  const [showAssignHostModal, setShowAssignHostModal] = useState(false);
  // A league night is put on the calendar before its course is necessarily scoreable: the
  // week's lineup only snapshots into pingolf_event_holes once every hole has a target score
  // (the column is NOT NULL). Until then the night has zero holes and every card purchase 400s.
  // The week's lineup is the thing to fix, so this page loads it rather than sending the host
  // off to League Admin to find out what is wrong.
  const [week, setWeek] = useState(null);
  const [machines, setMachines] = useState([]);
  const [lineupDraft, setLineupDraft] = useState(null);
  const [savingLineup, setSavingLineup] = useState(false);
  // Closing sales or submissions mid-night is the one lifecycle move a host can make by
  // accident and cannot silently undo for a player already mid-purchase, so it confirms.
  const [lifecycleConfirm, setLifecycleConfirm] = useState(null);
  const [holeStats, setHoleStats] = useState([]);
  const [holeStatsScope, setHoleStatsScope] = useState('event');
  const [holeStatsError, setHoleStatsError] = useState(false);
  const [selectedStatHoleId, setSelectedStatHoleId] = useState(null);

  useEffect(() => {
    fetchEvent();
    fetchHoleStats();
    if (isAdmin) {
      fetchEligibleHosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isAdmin]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/events/${eventId}`);
      setEvent(response.data);
      setError(null);

      // Only when the night came up short of a course - a night with its own holes is
      // self-contained and has no reason to read the week.
      const seasonWeekId = response.data?.season_week_id;
      if (seasonWeekId && (response.data?.pingolf_holes || []).length === 0) {
        await fetchWeekCourse(seasonWeekId);
      } else if ((response.data?.pingolf_holes || []).length > 0
        && !response.data?.event_metadata?.pingolf?.lineup_locked_at) {
        // A course that can still gain a hole needs the machine list to pick from.
        await fetchMachines();
      }
    } catch (err) {
      console.error('Error loading event host controls:', err);
      setError('Failed to load host controls.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await apiService.get('/machines');
      setMachines(response.data || []);
    } catch (err) {
      console.error('Error loading machines:', err);
    }
  };

  // Only needed when the night has no course of its own; machine names come along because the
  // lineup stores machine ids and a row reading "Hole 3" with no machine is unfixable.
  const fetchWeekCourse = async (seasonWeekId) => {
    try {
      const [weekResponse, machinesResponse] = await Promise.all([
        apiService.get(`/pingolf-seasons/weeks/${seasonWeekId}`),
        apiService.get('/machines')
      ]);
      setWeek(weekResponse.data);
      setMachines(machinesResponse.data || []);
    } catch (err) {
      console.error('Error loading the week lineup:', err);
    }
  };

  const fetchHoleStats = async () => {
    try {
      const response = await apiService.get(`/events/${eventId}/pingolf-hole-stats`);
      setHoleStats(response.data?.holes || []);
      setHoleStatsScope(response.data?.scope === 'week' ? 'week' : 'event');
      setHoleStatsError(false);
    } catch (err) {
      // Supplementary reading only - a failure here must not take the controls down with it.
      // It does get reported, though: silently falling back to an empty list made a failed
      // request indistinguishable from a night nobody has played yet.
      console.error('Error loading Pingolf hole stats:', err);
      setHoleStats([]);
      setHoleStatsError(true);
    }
  };

  const fetchEligibleHosts = async () => {
    try {
      const response = await apiService.get('/users?hostEligible=true');
      setEligibleHosts(response.data || []);
    } catch (err) {
      console.error('Error loading eligible hosts:', err);
      setError('Failed to load eligible hosts.');
    }
  };

  const canManage = useMemo(() => {
    if (isAdmin) {
      return true;
    }

    if (!event?.hosts || !currentUser?.id) {
      return false;
    }

    return event.hosts.some((host) => Number(host.id) === Number(currentUser.id));
  }, [event?.hosts, currentUser?.id, isAdmin]);

  const isPingolfEvent = event?.event_metadata?.pinball_format === 'pingolf';
  const lifecycle = event?.event_metadata?.pingolf?.lifecycle || {};
  const salesOpen = lifecycle.sales_open === true;
  const submissionsOpen = lifecycle.submissions_open === true;
  const isFinalized = Boolean(event?.event_metadata?.pingolf?.finalized_at);
  const isCanceled = Boolean(event?.event_metadata?.pingolf?.canceled_at);
  const holeList = event?.pingolf_holes || [];
  const cardsSold = Number(event?.cards_sold ?? 0);
  // A night that belongs to a season week is settled by the week, not on its own - the pot is
  // pooled across the week's sessions and each card counts once toward a handicap. The server
  // refuses a per-event finalize for these; this keeps the button from offering it at all.
  const leagueWeekNumber = event?.week_number ?? null;
  const isLeagueNight = leagueWeekNumber !== null && leagueWeekNumber !== undefined;
  const canFinalize = !salesOpen && !submissionsOpen && !isFinalized && !isCanceled && !isLeagueNight;
  const seasonId = event?.season_id ?? null;
  const seasonName = event?.season_name || null;
  // Where the week actually gets settled. Falls back to the season list when the event row
  // somehow has no season id, so the button always goes somewhere useful.
  const leagueAdminPath = seasonId ? `/pingolf-season/admin/${seasonId}` : '/pingolf-season/admin';
  // The course is what makes a night sellable: no holes means every purchase is refused
  // server-side, so this is the one precondition a host has to clear before opening sales.
  const courseSet = holeList.length > 0;
  const seasonWeekId = event?.season_week_id ?? null;
  const weekLineup = week?.lineup || [];
  const machineNameById = new Map(machines.map((machine) => [Number(machine.id), machine.name]));
  const lineupHolesMissingTarget = weekLineup.filter((hole) => !(Number(hole.targetScore) > 0)).length;
  // Setting the week's lineup is a league-manager action (the API enforces it) - the same
  // check FeatureRoleGuard makes for the League Admin route. A plain host gets the diagnosis
  // and a pointer rather than a form the server would reject.
  const canManageLeague = isAdmin || (currentUser?.featureRoles || []).includes('penthouse_league');
  // Both hole lists are editable in place rather than behind an edit toggle, so who may edit
  // (and when) is what decides whether the rows are tappable at all.
  const canLineupEdit = canManageLeague && !isFinalized && !isCanceled;
  const canHolesEdit = !isFinalized && !isCanceled;
  // Targets stay editable all night; which machines make up the course does not. The server
  // stamps lineup_locked_at on the first card sale and refuses add/remove after it, so the
  // UI reads the same flag rather than inventing a second rule.
  const courseLocked = Boolean(event?.event_metadata?.pingolf?.lineup_locked_at) || cardsSold > 0;
  const canCourseStructureEdit = canHolesEdit && !courseLocked;
  const assignedHostIds = new Set((event?.hosts || []).map((host) => Number(host.id)));
  const availableHostOptions = eligibleHosts.filter((user) => !assignedHostIds.has(Number(user.id)));

  const selectedStatHole = useMemo(() => {
    if (holeStats.length === 0) {
      return null;
    }

    return holeStats.find((hole) => hole.hole_id === selectedStatHoleId) || holeStats[0];
  }, [holeStats, selectedStatHoleId]);

  const strokeBars = useMemo(() => {
    const buckets = selectedStatHole?.buckets || [];
    const peak = Math.max(0, ...buckets.map((bucket) => bucket.count));

    return buckets.map((bucket) => ({
      label: formatVsPar(bucket.result),
      count: bucket.count,
      // Zero or under is the target reached, one or more is a miss. Tinting the two halves
      // differently is what makes the chart answer "is this target set right" at a glance.
      isReached: bucket.result <= 0,
      // An empty bucket still gets a sliver so the baseline reads as a chart, not a gap.
      heightPercent: peak > 0 && bucket.count > 0 ? Math.round(8 + (bucket.count / peak) * 92) : 2,
      isPeak: peak > 0 && bucket.count === peak
    }));
  }, [selectedStatHole]);

  const updateLifecycle = async (patch, successMessage) => {
    setUpdatingLifecycle(true);
    try {
      await apiService.put(`/events/${eventId}/pingolf-lifecycle`, patch);
      setLifecycleConfirm(null);
      setError(null);
      await fetchEvent();
      if (successMessage) {
        showToast(successMessage, 'success');
      }
    } catch (err) {
      console.error('Error updating Pingolf lifecycle:', err);
      setError(err.response?.data?.error || 'Failed to update lifecycle.');
    } finally {
      setUpdatingLifecycle(false);
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      const response = await apiService.post(`/events/${eventId}/pingolf-finalize`);
      setOperationSummary({ type: 'finalize', data: response.data });
      await fetchEvent();
    } catch (err) {
      console.error('Error finalizing event:', err);
      setError(err.response?.data?.error || 'Failed to finalize event.');
    } finally {
      setFinalizing(false);
    }
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      const response = await apiService.post(`/events/${eventId}/pingolf-cancel`);
      setOperationSummary({ type: 'cancel', data: response.data });
      await fetchEvent();
    } catch (err) {
      console.error('Error canceling event:', err);
      setError(err.response?.data?.error || 'Failed to cancel event.');
    } finally {
      setCanceling(false);
    }
  };

  // The list is always editable, so there is no "start editing" step - the draft only
  // exists between a first keystroke and the save that follows it, and the rendered list
  // falls back to the week's own lineup whenever there is no draft in flight.
  const lineupHoles = () => weekLineup.map((hole, index) => ({
    key: index,
    machineId: hole.machineId,
    holeNumber: index + 1,
    name: machineNameById.get(Number(hole.machineId)) || `Machine #${hole.machineId}`,
    targetScore: hole.targetScore === null || hole.targetScore === undefined ? '' : String(hole.targetScore),
    maxBalls: String(hole.maxBalls ?? 3)
  }));

  const updateDraftLineupHole = (key, field, value) => {
    setLineupDraft((prev) => (prev || lineupHoles()).map((hole) => {
      if (hole.key !== key) return hole;
      if (field !== 'machineId') return { ...hole, [field]: value };
      // Picking a machine brings its saved target and ball count with it, same as in
      // League Admin - the point of those fields on the machine record.
      const machine = machines.find((m) => String(m.id) === String(value));
      return {
        ...hole,
        machineId: value,
        targetScore: machine?.pingolf_target_score != null
          ? String(machine.pingolf_target_score)
          : hole.targetScore,
        maxBalls: String(machine?.pingolf_ball_count || hole.maxBalls || 3)
      };
    }));
  };

  // Add and remove write through the same week-lineup PUT as an edit, so a hole added
  // here reaches every night in the week exactly like a target does.
  const addLineupHole = () => {
    setLineupDraft((prev) => {
      const current = prev || lineupHoles();
      return [...current, {
        key: current.length,
        machineId: '',
        holeNumber: current.length + 1,
        name: '',
        targetScore: '',
        maxBalls: '3'
      }];
    });
  };

  const removeLineupHole = (key) => {
    const current = lineupDraft || lineupHoles();
    const next = current
      .filter((hole) => hole.key !== key)
      .map((hole, index) => ({ ...hole, key: index, holeNumber: index + 1 }));
    setLineupDraft(next);
    saveLineup(next);
  };

  // Saves back to the WEEK, not the event: the lineup is shared by every night in the week, and
  // setWeekLineup snapshots it into any session still missing its course once every hole has a
  // target. So filling these in here is what actually opens this night (and its sibling) up for
  // sales - there is no per-event equivalent while the event has no hole rows to edit.
  const saveLineup = async (draft) => {
    const pending = draft || lineupDraft;
    if (!pending) return;

    // A hole with no machine picked yet is a normal in-between state (Add hole, then
    // choose), so it is left out of the write rather than blocking it.
    const filled = pending.filter((hole) => hole.machineId !== '' && hole.machineId != null);

    for (const hole of filled) {
      const parsedMaxBalls = parseInt(hole.maxBalls, 10);
      if (Number.isNaN(parsedMaxBalls) || parsedMaxBalls < 1) {
        setError('Max balls must be a whole number greater than zero for every hole.');
        return;
      }
      if (hole.targetScore !== '' && !(parseInt(hole.targetScore, 10) > 0)) {
        setError('Target score must be a whole number greater than zero.');
        return;
      }
    }

    setSavingLineup(true);
    try {
      await apiService.put(`/pingolf-seasons/weeks/${seasonWeekId}/lineup`, {
        lineup: filled.map((hole) => ({
          machineId: Number(hole.machineId),
          targetScore: hole.targetScore === '' ? null : parseInt(hole.targetScore, 10),
          maxBalls: parseInt(hole.maxBalls, 10)
        }))
      });
      setLineupDraft(null);
      setError(null);
      await fetchEvent();
      await fetchHoleStats();
      showToast('Course saved', 'success');
    } catch (err) {
      console.error('Error saving the week lineup:', err);
      setError(err.response?.data?.error || 'Failed to save the course.');
    } finally {
      setSavingLineup(false);
    }
  };

  // Same as the lineup above: no edit toggle, and a draft row only appears for a hole
  // once it has actually been touched.
  const draftFor = (holeId) => draftHoles.find((h) => h.id === holeId);

  const updateDraftHole = (holeId, field, value) => {
    setDraftHoles((prev) => {
      const existing = prev.find((h) => h.id === holeId);
      if (existing) {
        return prev.map((h) => (h.id === holeId ? { ...h, [field]: value } : h));
      }
      const hole = holeList.find((h) => h.id === holeId);
      return [...prev, {
        id: holeId,
        targetScore: String(hole?.target_score ?? ''),
        maxBalls: String(hole?.max_balls ?? ''),
        [field]: value
      }];
    });
  };

  // One hole, one PUT, when that hole's sheet closes. The endpoint is per-hole anyway and
  // each write recomputes any card results already scored against the hole, so saving only
  // what changed is both the smaller write and the smaller recompute.
  const saveHole = async (holeId) => {
    const draft = draftFor(holeId);
    if (!draft) return;

    const parsedMaxBalls = parseInt(draft.maxBalls, 10);
    if (Number.isNaN(parsedMaxBalls) || parsedMaxBalls < 1) {
      setError('Max balls must be a whole number greater than zero.');
      return;
    }
    if (draft.targetScore !== '' && Number.isNaN(parseInt(draft.targetScore, 10))) {
      setError('Target score must be a number.');
      return;
    }

    setSavingHoles(true);
    try {
      await apiService.put(`/events/${eventId}/pingolf-holes/${draft.id}`, {
        targetScore: draft.targetScore === '' ? undefined : parseInt(draft.targetScore, 10),
        maxBalls: parsedMaxBalls
      });
      setError(null);
      setDraftHoles((prev) => prev.filter((h) => h.id !== holeId));
      await fetchEvent();
      await fetchHoleStats();
      showToast('Hole updated', 'success');
    } catch (err) {
      console.error('Error updating hole:', err);
      setError(err.response?.data?.error || 'Failed to update the hole.');
    } finally {
      setSavingHoles(false);
    }
  };

  // Puts a hole with its own target back on its machine's target, so it follows changes to the
  // machine's target again. The server refuses it once the night is locked, like any target edit.
  const resetHoleToMachineTarget = async (holeId) => {
    setSavingHoles(true);
    try {
      await apiService.put(`/events/${eventId}/pingolf-holes/${holeId}`, { useMachineTarget: true });
      setError(null);
      setDraftHoles((prev) => prev.filter((h) => h.id !== holeId));
      await fetchEvent();
      await fetchHoleStats();
      showToast('Hole is back on its machine target', 'success');
    } catch (err) {
      console.error('Error resetting hole target:', err);
      setError(err.response?.data?.error || 'Failed to reset the hole target.');
    } finally {
      setSavingHoles(false);
    }
  };

  // Picking a machine brings its saved target and ball count with it, exactly as it does in
  // League Admin - both stay editable before the hole is written.
  const changeNewHole = (field, value) => {
    setNewHole((prev) => {
      if (field !== 'machineId') return { ...prev, [field]: value };
      const machine = machines.find((m) => String(m.id) === String(value));
      return {
        machineId: value,
        targetScore: machine?.pingolf_target_score != null ? String(machine.pingolf_target_score) : '',
        maxBalls: String(machine?.pingolf_ball_count || 3)
      };
    });
  };

  const addCourseHole = async () => {
    if (!newHole.machineId) {
      setError('Pick a machine to add as a hole.');
      return;
    }

    const parsedMaxBalls = parseInt(newHole.maxBalls, 10);
    if (Number.isNaN(parsedMaxBalls) || parsedMaxBalls < 1) {
      setError('Max balls must be a whole number greater than zero.');
      return;
    }
    if (newHole.targetScore !== '' && !(parseInt(newHole.targetScore, 10) > 0)) {
      setError('Target score must be a whole number greater than zero.');
      return;
    }

    setSavingHoles(true);
    try {
      await apiService.post(`/events/${eventId}/pingolf-holes`, {
        machineId: Number(newHole.machineId),
        targetScore: newHole.targetScore === '' ? undefined : parseInt(newHole.targetScore, 10),
        maxBalls: parsedMaxBalls
      });
      setShowAddHoleSheet(false);
      setNewHole({ machineId: '', targetScore: '', maxBalls: '3' });
      setError(null);
      await fetchEvent();
      await fetchHoleStats();
      showToast('Hole added', 'success');
    } catch (err) {
      console.error('Error adding a hole:', err);
      setError(err.response?.data?.error || 'Failed to add the hole.');
    } finally {
      setSavingHoles(false);
    }
  };

  const removeCourseHole = async (holeId) => {
    setSavingHoles(true);
    try {
      await apiService.delete(`/events/${eventId}/pingolf-holes/${holeId}`);
      setDraftHoles((prev) => prev.filter((h) => h.id !== holeId));
      setError(null);
      await fetchEvent();
      await fetchHoleStats();
      showToast('Hole removed', 'success');
    } catch (err) {
      console.error('Error removing a hole:', err);
      setError(err.response?.data?.error || 'Failed to remove the hole.');
    } finally {
      setSavingHoles(false);
    }
  };

  const handleAssignHost = async () => {
    if (!selectedHostUserId) {
      setError('Select a user to assign as host.');
      return;
    }

    setAssigningHost(true);
    try {
      await apiService.post(`/events/${eventId}/assign-host`, {
        userId: parseInt(selectedHostUserId, 10)
      });
      setSelectedHostUserId('');
      setError(null);
      setShowAssignHostModal(false);
      await fetchEvent();
      await fetchEligibleHosts();
      showToast('Host added', 'success');
    } catch (err) {
      console.error('Error assigning host:', err);
      setError(err.response?.data?.error || 'Failed to assign host.');
    } finally {
      setAssigningHost(false);
    }
  };

  const handleRemoveHost = async () => {
    if (!hostRemovalTarget?.id) {
      return;
    }

    setRemovingHostId(hostRemovalTarget.id);
    try {
      await apiService.delete(`/events/${eventId}/remove-host/${hostRemovalTarget.id}`);
      setError(null);
      setHostRemovalTarget(null);
      await fetchEvent();
      await fetchEligibleHosts();
      showToast('Host removed', 'success');
    } catch (err) {
      console.error('Error removing host:', err);
      setError(err.response?.data?.error || 'Failed to remove host.');
    } finally {
      setRemovingHostId(null);
    }
  };

  const hostDisplayName = (host) => (
    personName(host) || host.email
  );

  // The hole card's one onward action, whichever branch of the course state you are in.
  // Computed here rather than inside the branches because it is the card's footer now, and
  // a footer that moved around inside the body depending on which case rendered was how
  // three different versions of "+ Add hole" ended up in three places. It has to sit below
  // the handlers it calls: built any earlier, a night with a week lineup but no course yet
  // read addLineupHole before it existed and crashed the page.
  const holeConfigFooter = (() => {
    if (!courseSet && weekLineup.length > 0) {
      return canLineupEdit ? (
        <div className="pingolf-footer-end">
          <DSButton appearance="text" tone="neutral" size="sm" onClick={addLineupHole} disabled={savingLineup}>
            + Add hole
          </DSButton>
        </div>
      ) : (
        <div className="pingolf-footer-end">
          <DSButton
            appearance="outline"
            tone="secondary"
            size="sm"
            onClick={() => navigate(leagueAdminPath)}
            trailingIcon={<DSIcon name="arrowRight" size="sm" tone="default" />}
          >
            Ask the organizer (League Admin)
          </DSButton>
        </div>
      );
    }

    if (courseSet && canCourseStructureEdit) {
      return (
        <div className="pingolf-footer-end">
          <DSButton
            appearance="text"
            tone="neutral"
            size="sm"
            onClick={() => setShowAddHoleSheet(true)}
            disabled={savingHoles}
          >
            + Add hole
          </DSButton>
        </div>
      );
    }

    return null;
  })();


  if (loading) {
    return (
      <div className="pingolf-host-controls-page">
        <DSCard title="Loading host controls" subtitle="Pulling lifecycle state, hole settings, and event totals." />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="pingolf-host-controls-page">
        <DSAlert variant="error" appearance="outline">Event not found.</DSAlert>
      </div>
    );
  }

  if (!isPingolfEvent) {
    return (
      <div className="pingolf-host-controls-page">
        <DSAlert variant="error" appearance="outline">This page is only available for PinGolf events.</DSAlert>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="pingolf-host-controls-page">
        <DSAlert variant="error" appearance="outline">You do not have permission to manage host controls for this event.</DSAlert>
      </div>
    );
  }

  const confirmCopy = {
    closeSales: {
      title: 'Close card sales?',
      body: 'Players will no longer be able to buy a card for tonight. You can reopen sales at any time.',
      confirmLabel: 'Close sales',
      patch: { salesOpen: false },
      successMessage: 'Card sales closed'
    },
    closeSubmissions: {
      title: 'Close submissions?',
      body: 'Scores already submitted are kept. Players will not be able to submit anything new until you reopen.',
      confirmLabel: 'Close submissions',
      patch: { submissionsOpen: false },
      successMessage: 'Submissions closed'
    }
  };

  const activeConfirm = lifecycleConfirm ? confirmCopy[lifecycleConfirm] : null;

  return (
    <div className="pingolf-host-controls-page">
        <div className="pingolf-host-back-row">
          <DSBackLink to={`/events/${eventId}`}>Back to Event</DSBackLink>
        </div>

        <DSPageHeader
          title={event.title}
          eyebrow="Host Controls"
          actions={(
            <div className="pingolf-host-header-badges">
              {/* Closed is the resting state of a night that has not started, not a fault,
                  so it reads neutral rather than as an error. */}
              <DSBadge variant={salesOpen ? 'success' : 'neutral'} appearance="outline">
                Sales {salesOpen ? 'open' : 'closed'}
              </DSBadge>
              <DSBadge variant={submissionsOpen ? 'success' : 'neutral'} appearance="outline">
                Submissions {submissionsOpen ? 'open' : 'closed'}
              </DSBadge>
            </div>
          )}
        />

        {error ? <DSAlert variant="error" appearance="outline">{error}</DSAlert> : null}

        {operationSummary?.type === 'finalize' ? (
          <DSAlert variant="success" appearance="outline">
            Finalized: ${operationSummary.data.gross_pool} pool, ${operationSummary.data.total_awards} awards, ${operationSummary.data.house_remainder} remainder.
          </DSAlert>
        ) : null}

        {operationSummary?.type === 'cancel' ? (
          <DSAlert variant="warning" appearance="outline">
            Canceled: Refunded {operationSummary.data.refunded_cards} cards totaling ${operationSummary.data.refunded_total}.
          </DSAlert>
        ) : null}

        {/* Raised here, at the top, because the failure it describes shows up as a player being
            refused at purchase - which the host only hears about second-hand, after the fact. */}
        {!courseSet && !isCanceled && !isFinalized ? (
          <DSAlert variant="warning" appearance="outline">
            <strong>No course set for this night.</strong> Cards cannot be sold until every hole has a
            target score{lineupHolesMissingTarget > 0 ? `, and ${lineupHolesMissingTarget} of the week's ${weekLineup.length} holes still need one` : ''}.
            {' '}Fix it under Hole Configuration below.
          </DSAlert>
        ) : null}

        <DSCard
          title="Event state"
          className="pingolf-section-card"
          frame="bars"
          headerTone="strong"
        >
          <div className="pingolf-state-list">
            <div className="pingolf-state-row">
              <div className="pingolf-state-copy">
                <strong>Card sales</strong>
                <span className={salesOpen ? 'pingolf-state-open' : 'pingolf-state-closed'}>
                  {salesOpen ? 'Open, players can buy cards' : 'Closed, no new cards'}
                </span>
                {!courseSet && !salesOpen ? (
                  <span className="pingolf-status-note">Set the course before opening sales.</span>
                ) : null}
              </div>
              {salesOpen ? (
                <DSButton
                  appearance="outline"
                  tone="neutral"
                  onClick={() => setLifecycleConfirm('closeSales')}
                  disabled={updatingLifecycle || isFinalized || isCanceled}
                >
                  Close sales
                </DSButton>
              ) : (
                <DSButton
                  appearance="solid"
                  tone="primary"
                  onClick={() => updateLifecycle({ salesOpen: true }, 'Card sales opened')}
                  // Opening sales on a course-less night only produces a player who pays nothing
                  // and gets an error. Closing stays available whatever the course looks like.
                  disabled={updatingLifecycle || isFinalized || isCanceled || !courseSet}
                  title={!courseSet ? 'This night has no course set yet' : undefined}
                >
                  Open sales
                </DSButton>
              )}
            </div>

            <div className="pingolf-state-row">
              <div className="pingolf-state-copy">
                <strong>Submissions</strong>
                <span className={submissionsOpen ? 'pingolf-state-open' : 'pingolf-state-closed'}>
                  {submissionsOpen ? 'Open, scores accepted' : 'Closed, no new scores'}
                </span>
              </div>
              {submissionsOpen ? (
                <DSButton
                  appearance="outline"
                  tone="neutral"
                  onClick={() => setLifecycleConfirm('closeSubmissions')}
                  disabled={updatingLifecycle || isFinalized || isCanceled}
                >
                  Close submissions
                </DSButton>
              ) : (
                <DSButton
                  appearance="solid"
                  tone="primary"
                  onClick={() => updateLifecycle({ submissionsOpen: true }, 'Submissions opened')}
                  disabled={updatingLifecycle || isFinalized || isCanceled}
                >
                  Open submissions
                </DSButton>
              )}
            </div>
          </div>
        </DSCard>

        {/* A league night has nothing to finalize here - the pot is pooled across the week and
            settled once, in league admin. A standalone night still settles itself. */}
        {isLeagueNight ? (
          <section className="pingolf-league-callout">
            <strong>Prizes are settled in league admin</strong>
            <p>
              Settled with the rest of Week {leagueWeekNumber}. The pot is shared across every night
              this week, so prizes are awarded when the week is finalized from
              {seasonName ? ` ${seasonName} admin` : ' League Admin'}, not per night.
            </p>
            <DSButton
              appearance="outline"
              tone="secondary"
              onClick={() => navigate(leagueAdminPath)}
              trailingIcon={<DSIcon name="arrowRight" size="sm" tone="brand" />}
            >
              Go to League Admin
            </DSButton>
          </section>
        ) : (
          <DSCard
            title="Finalize Event"
            subtitle="Close sales and submissions before finalizing."
            className="pingolf-section-card"
            frame="bars"
            headerTone="strong"
          >
            <div className="pingolf-settle-row">
              <p>Award prizes and close the event.</p>
              <DSButton
                appearance="solid"
                tone="neutral"
                onClick={handleFinalize}
                disabled={finalizing || canceling || !canFinalize}
                leadingIcon={<DSIcon name="trophy" size="sm" tone="default" />}
              >
                {finalizing ? 'Finalizing...' : 'Finalize & Award Prizes'}
              </DSButton>
            </div>
          </DSCard>
        )}

        {holeStatsError ? (
          <DSCard
            title="Results by Hole"
            className="pingolf-section-card"
            frame="bars"
            headerTone="strong"
          >
            <DSAlert variant="warning" appearance="outline">
              Could not load hole results. The rest of the controls on this page still work.
            </DSAlert>
          </DSCard>
        ) : null}

        {!holeStatsError && holeStats.length > 0 ? (
          <DSCard
            title="Results by Hole"
            subtitle={holeStatsScope === 'week'
              ? "Every card played this week, across all of the week's host nights. Pick a hole."
              : 'Scores recorded so far tonight. Pick a hole.'}
            className="pingolf-section-card"
            frame="bars"
            headerTone="strong"
          >
            <div className="pingolf-hole-chips">
              {holeStats.map((hole) => {
                const isActive = selectedStatHole?.hole_id === hole.hole_id;
                return (
                  <button
                    key={hole.hole_id}
                    type="button"
                    className={`pingolf-hole-chip${isActive ? ' pingolf-hole-chip--active' : ''}`}
                    aria-pressed={isActive}
                    onClick={() => setSelectedStatHoleId(hole.hole_id)}
                  >
                    {hole.hole_number}
                  </button>
                );
              })}
            </div>

            {selectedStatHole ? (
              <>
                <div className="pingolf-stat-hole-heading">
                  <strong>{selectedStatHole.machine_name}</strong>
                  <span>
                    Hole {selectedStatHole.hole_number}, target {formatScoreAbbreviated(Number(selectedStatHole.target_score))}, {selectedStatHole.max_balls} balls
                  </span>
                </div>

                {selectedStatHole.submissions > 0 ? (
                  <>
                    <div className="pingolf-histogram">
                      {strokeBars.map((bar) => (
                        <div key={bar.label} className="pingolf-histogram-column">
                          <span className="pingolf-histogram-count">{bar.count}</span>
                          <span
                            className={[
                              'pingolf-histogram-bar',
                              bar.isReached ? 'pingolf-histogram-bar--reached' : 'pingolf-histogram-bar--missed',
                              bar.isPeak ? 'pingolf-histogram-bar--peak' : ''
                            ].filter(Boolean).join(' ')}
                            style={{ height: `${bar.heightPercent}%` }}
                          />
                          <span className="pingolf-histogram-label">{bar.label}</span>
                        </div>
                      ))}
                    </div>
                    <p className="pingolf-histogram-axis">Score vs par. Zero and under reached the target.</p>
                  </>
                ) : (
                  <p className="pingolf-hole-note">
                    {holeStatsScope === 'week'
                      ? 'No scores recorded on this hole yet this week.'
                      : 'No scores recorded on this hole yet.'}
                  </p>
                )}

                <div className="pingolf-hole-stat-tiles">
                  <div className="pingolf-hole-stat-tile">
                    <span>Scores</span>
                    <strong>{selectedStatHole.submissions}</strong>
                  </div>
                  <div className="pingolf-hole-stat-tile">
                    <span>Reached</span>
                    <strong>
                      {selectedStatHole.submissions
                        ? `${selectedStatHole.reached} of ${selectedStatHole.submissions}`
                        : '-'}
                    </strong>
                  </div>
                  <div className="pingolf-hole-stat-tile">
                    <span>Average</span>
                    <strong>{formatVsPar(selectedStatHole.average)}</strong>
                  </div>
                  <div className="pingolf-hole-stat-tile">
                    <span>Best</span>
                    <strong>{formatVsPar(selectedStatHole.best)}</strong>
                  </div>
                </div>
              </>
            ) : null}
          </DSCard>
        ) : null}

        {showAssignHostModal && (
          <DSModal
            title="Assign Host"
            size="sm"
            onClose={() => {
              if (!assigningHost) {
                setShowAssignHostModal(false);
              }
            }}
          >
            <DSSelect
              label="Assign Host"
              value={selectedHostUserId}
              onChange={(eventChange) => setSelectedHostUserId(eventChange.target.value)}
            >
              <option value="">Select a user</option>
              {availableHostOptions.map((user) => (
                <option key={user.id} value={user.id}>
                  {personName(user) || user.email} ({user.email})
                </option>
              ))}
            </DSSelect>
            <div className="pingolf-host-modal-actions">
              <DSButton
                appearance="outline"
                tone="neutral"
                onClick={() => setShowAssignHostModal(false)}
                disabled={assigningHost}
              >
                Cancel
              </DSButton>
              <DSButton
                appearance="solid"
                tone="primary"
                onClick={handleAssignHost}
                disabled={assigningHost || !selectedHostUserId}
                leadingIcon={<DSIcon name="plus" size="sm" tone="default" />}
              >
                {assigningHost ? 'Assigning...' : 'Assign Host'}
              </DSButton>
            </div>
          </DSModal>
        )}

        <DSCard
          className="pingolf-section-card"
          frame="bars"
          headerTone="strong"
          flush
          title="Hole Configuration"
          subtitle={courseSet
            ? `Target scores lock after the first sale${cardsSold > 0 ? ` (${cardsSold} sold)` : ''}. Review max balls carefully before live play.`
            : `This night has no course yet. Set a target score on every hole to create it. The week's lineup becomes this night's course once every hole has a target.`}
          actions={(
            <DSBadge variant={courseSet ? 'info' : 'warning'} appearance="outline">
              {courseSet
                ? `${holeList.length} holes`
                : weekLineup.length > 0
                  ? `${lineupHolesMissingTarget} of ${weekLineup.length} missing a target`
                  : 'No course'}
            </DSBadge>
          )}
          footer={holeConfigFooter}
        >

          {/* No course, but the week has a lineup: the fixable case, and the one that used to
              read "0 holes" with an Edit Holes button that opened an empty list. */}
          {!courseSet && weekLineup.length > 0 ? (
            <>
              {canLineupEdit ? (
                <>
                  <PinGolfHoleList
                    editing
                    machines={machines}
                    disabled={savingLineup}
                    holes={lineupDraft || lineupHoles()}
                    onChangeHole={updateDraftLineupHole}
                    onCommit={() => saveLineup()}
                    onRemoveHole={removeLineupHole}
                  />
                  <p className="pingolf-hole-note">
                    This lineup is shared by every night in Week {leagueWeekNumber}, so a hole added,
                    removed or retargeted here changes all of them.
                  </p>
                </>
              ) : (
                <>
                  <PinGolfHoleList holes={lineupHoles()} />
                </>
              )}
            </>
          ) : null}

          {!courseSet && weekLineup.length === 0 ? (
            <p className="pingolf-hole-note">
              {seasonWeekId
                ? 'This week has no lineup yet. The league organizer picks the machines in League Admin.'
                : 'This night has no holes. Add machines to the event to build its course.'}
            </p>
          ) : null}

          {courseSet ? (
            <>
              <PinGolfHoleList
                editing={canHolesEdit}
                disabled={savingHoles}
                holes={holeList.map((hole) => {
                  const draft = draftFor(hole.id);
                  return {
                    key: hole.id,
                    holeNumber: hole.hole_number,
                    name: hole.machine_name,
                    // An untouched hole reads from the event; a touched one reads from its
                    // draft, which is what the sheet is editing until its save lands.
                    targetScore: draft ? draft.targetScore : Number(hole.target_score),
                    maxBalls: draft ? draft.maxBalls : hole.max_balls,
                    targetOverridden: Boolean(hole.target_overridden),
                    machineTargetScore: hole.machine_target_score ?? null
                  };
                })}
                onChangeHole={updateDraftHole}
                onUseMachineTarget={canCourseStructureEdit ? resetHoleToMachineTarget : undefined}
                onCommit={saveHole}
                onRemoveHole={canCourseStructureEdit ? removeCourseHole : undefined}
              />
              {canCourseStructureEdit ? null : (
                <p className="pingolf-hole-note">
                  {cardsSold > 0
                    ? 'A card has been sold, so the machines in this course are fixed. Targets and max balls can still be corrected.'
                    : 'This night is closed, so its course can no longer change.'}
                </p>
              )}
            </>
          ) : null}
        </DSCard>

        {showAddHoleSheet ? (
          <PinGolfHoleSheet
            title="Add a hole"
            hole={newHole}
            // Machines already on this night are filtered out: pingolf_event_holes is
            // UNIQUE per machine, so offering one would only produce a rejected add.
            machines={machines.filter((machine) => (
              !holeList.some((hole) => Number(hole.machine_id) === Number(machine.id))
            ))}
            disabled={savingHoles}
            onChange={changeNewHole}
            onClose={() => setShowAddHoleSheet(false)}
            footer={(
              <>
                <DSButton
                  appearance="outline"
                  tone="neutral"
                  onClick={() => setShowAddHoleSheet(false)}
                  disabled={savingHoles}
                >
                  Cancel
                </DSButton>
                <DSButton onClick={addCourseHole} disabled={savingHoles || !newHole.machineId}>
                  {savingHoles ? 'Adding...' : 'Add hole'}
                </DSButton>
              </>
            )}
          />
        ) : null}

        <DSCard
          title="Hosts"
          className="pingolf-section-card"
          frame="bars"
          headerTone="strong"
          flush
          subtitle={isAdmin ? 'Assign hosts and maintain event ownership.' : 'Current host roster for this event.'}
          footer={isAdmin ? (
            <div className="pingolf-footer-end">
              <DSButton
                appearance="outline"
                tone="neutral"
                size="sm"
                onClick={() => setShowAssignHostModal(true)}
                leadingIcon={<DSIcon name="plus" size="sm" tone="default" />}
              >
                Add host
              </DSButton>
            </div>
          ) : null}
        >
          <div className="pingolf-host-list">
            {(event.hosts || []).length > 0 ? (
              event.hosts.map((host) => (
                <div key={host.id} className="pingolf-host-row">
                  <UserAvatar person={host} className="pingolf-host-avatar" />
                  <div className="pingolf-host-row-copy">
                    <strong>{hostDisplayName(host)}</strong>
                    <span>{host.email}</span>
                  </div>
                  {isAdmin ? (
                    <DSButton
                      appearance="text"
                      tone="danger"
                      size="sm"
                      onClick={() => setHostRemovalTarget(host)}
                      disabled={removingHostId === host.id}
                    >
                      Remove
                    </DSButton>
                  ) : null}
                </div>
              ))
            ) : (
              <p className="pingolf-host-empty">No hosts assigned yet.</p>
            )}
          </div>
        </DSCard>

        {/* Destructive and rarely wanted, so it sits below everything a host uses on the night
            rather than beside the buttons they press every week.

            Standalone nights only. A league night is one session of a week of a season - its
            cards, its pot and its standings are settled with the rest of the week, so
            canceling it in isolation is not a thing a host should be able to do from here.
            This control predates the league and only ever made sense for a one-off event. */}
        {!event.season_week_id ? (
        <section className="pingolf-danger-zone">
          <div>
            <strong>Cancel event</strong>
            <p>Refund all cards and cancel the event. This cannot be undone.</p>
          </div>
          <DSButton
            appearance="outline"
            tone="danger"
            onClick={handleCancel}
            disabled={canceling || finalizing || isFinalized || isCanceled}
            leadingIcon={<DSIcon name="x" size="sm" tone="default" />}
          >
            {canceling ? 'Canceling...' : 'Cancel Event & Refund'}
          </DSButton>
        </section>
        ) : null}

        {activeConfirm ? (
          <DSModal
            title={activeConfirm.title}
            size="sm"
            onClose={() => {
              if (!updatingLifecycle) {
                setLifecycleConfirm(null);
              }
            }}
          >
            <p className="pingolf-host-modal-copy">{activeConfirm.body}</p>
            <div className="pingolf-host-modal-actions">
              <DSButton
                appearance="text"
                tone="neutral"
                onClick={() => setLifecycleConfirm(null)}
                disabled={updatingLifecycle}
              >
                Keep as is
              </DSButton>
              <DSButton
                appearance="solid"
                tone="primary"
                onClick={() => updateLifecycle(activeConfirm.patch, activeConfirm.successMessage)}
                disabled={updatingLifecycle}
              >
                {updatingLifecycle ? 'Saving...' : activeConfirm.confirmLabel}
              </DSButton>
            </div>
          </DSModal>
        ) : null}

        {hostRemovalTarget ? (
          <DSModal
            title="Remove Host"
            size="sm"
            onClose={() => {
              if (!removingHostId) {
                setHostRemovalTarget(null);
              }
            }}
          >
            <p className="pingolf-host-modal-copy">
              Remove {hostDisplayName(hostRemovalTarget)} from this event?
            </p>
            <div className="pingolf-host-modal-actions">
              <DSButton
                appearance="outline"
                tone="neutral"
                onClick={() => setHostRemovalTarget(null)}
                disabled={Boolean(removingHostId)}
              >
                Cancel
              </DSButton>
              <DSButton
                appearance="solid"
                tone="danger"
                onClick={handleRemoveHost}
                disabled={Boolean(removingHostId)}
              >
                {removingHostId ? 'Removing...' : 'Remove Host'}
              </DSButton>
            </div>
          </DSModal>
        ) : null}
    </div>
  );
}

export default PingolfHostControls;